/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  FormsModule,
} from '@angular/forms';
import { NumberValidatorService } from '../../../../shared/services/number-validator/number-validator.service';
import { CommonModule } from '@angular/common';
import {
  LOAN_TYPE_OPTIONS,
  FINANCIAL_DOC_OPTIONS,
} from '../../../outreach/models/leads-criteria.model';

export interface OwnershipRow {
  ownerName: string;
  ownershipPercentage: number | undefined;
  otherBusinessesPercentage?: number | undefined;
}

@Component({
  selector: 'app-business-loan-form',
  standalone: true,
  imports: [ReactiveFormsModule, CommonModule, FormsModule],
  templateUrl: './business-loan-form.component.html',
  styleUrl: './business-loan-form.component.scss',
})
export class BusinessLoanFormComponent implements OnInit {
  @Input() initialValue: any;
  @Input() creditScoreRange: string | undefined;
  @Input() standalone = false;
  @Output() formSubmit = new EventEmitter<any>();

  isLoading = false;
  isSaving = false;
  businessRecordId: string | null = null;

  loanForm: FormGroup;
  private numberValidatorService = inject(NumberValidatorService);
  formSubmitted = false;
  showPersonalCapitalPercentage = false;
  showPrescreeningQuestion = false;
  showFullForm = false;
  showSimplifiedForm = false;
  ownershipStructure: OwnershipRow[] = [];

  loanTypeOptions = LOAN_TYPE_OPTIONS;
  financialDocOptions = FINANCIAL_DOC_OPTIONS;
  creditScoreOptions = [
    'Below 580',
    '580 and above',
    '670 and above',
    '740 and above',
    '800 and above',
  ];

  loanUseOptions = [
    'Business Acquisition',
    'Equipment Maintenance',
    'Business Expansion',
    'Equipment Purchase',
    'Real Estate Purchase',
    'Real Estate Construction',
    'Real Estate Improvements',
    'Refinance Business Debt',
    'Refinance Other Loan',
    'Solar Improvements',
    'Startup Activity',
    'Vehicle Purchase',
    'Purchase Inventory',
  ];

  collateralOptions = [
    'Real Estate - Building',
    'Land',
    'Equipment',
    'Vehicle',
    '3rd Party Guarantees',
    'Residential Real Estate',
    'Inventory',
    'Accounts Receivable',
    'Cash',
    'Investment Accounts',
    'Furniture and Fixtures',
    'New Equipment/Machinery',
    'Leasehold Improvements',
    'No Collateral',
  ];

  private collateralLabelMap: Record<string, string> = {
    'Real Estate - Building': 'real_estate_building',
    Land: 'land',
    Equipment: 'equipment',
    Vehicle: 'vehicle',
    '3rd Party Guarantees': 'third_party_guarantees',
    'Residential Real Estate': 'residential_real_estate',
    Inventory: 'inventory',
    'Accounts Receivable': 'accounts_receivable',
    Cash: 'cash',
    'Investment Accounts': 'investment_accounts',
    'Furniture and Fixtures': 'furniture_and_fixtures',
    'New Equipment/Machinery': 'new_equipment_machinery',
    'Leasehold Improvements': 'leasehold_improvements',
    'No Collateral': 'no_collateral',
  };

  private loanTypeLabelMap: Record<string, string> = {
    'Term Loans': 'term_loan',
    'Working Capital Loans': 'working_capital_loan',
    'Loan to purchase or upgrade a business facility or equipment':
      'business_facility_or_equipment_loan',
    'Refinance other Debt': 'refinance_other_debt',
    'Real Estate Lending (residential)': 'real_estate_lending_residential',
    'Commercial Real Estate Purchase': 'commercial_real_estate_purchase',
    'Start-up Support': 'start_up_support',
    'Short-term quick capital': 'short_term_quick_capital',
    'Business Acquisition': 'business_acquisition',
    'Grant Programs': 'grant_programs',
    'Venture Funding (Debt, equity, or Revenue Share)': 'venture_funding',
    'Acquisition / M&A': 'acquisition_m_a',
    'Project Finance': 'project_finance',
  };

  private financialDocLabelMap: Record<string, string> = {
    'Audited financial statements': 'audited_statements',
    'Unaudited formal financial statements': 'unaudited_statements',
    'Informal financial statements': 'informal_statements',
    'Tax returns': 'tax_returns',
    'No financial statements available': 'no_statements',
    'Bank Statements': 'bank_statements',
  };

  private loanUseLabelMap: Record<string, string> = {
    'Business Acquisition': 'business_acquisition',
    'Equipment Maintenance': 'equipment_maintenance',
    'Business Expansion': 'business_expansion',
    'Equipment Purchase': 'equipment_purchase',
    'Real Estate Purchase': 'real_estate_purchase',
    'Real Estate Construction': 'real_estate_construction',
    'Real Estate Improvements': 'real_estate_improvements',
    'Refinance Business Debt': 'refinance_business_debt',
    'Refinance Other Loan': 'refinance_other_loan',
    'Solar Improvements': 'solar_improvements',
    'Startup Activity': 'startup_activity',
    'Vehicle Purchase': 'vehicle_purchase',
    'Purchase Inventory': 'purchase_inventory',
  };

  private creditScoreRangeMap: Record<string, string> = {
    'Below 580': '<580',
    '580 and above': '580+',
    '670 and above': '670+',
    '740 and above': '740+',
    '800 and above': '800+',
  };

  constructor(private fb: FormBuilder) {
    this.loanForm = this.fb.group({
      // Loan Section
      stateRegisteredEntity: [''],
      loanType: this.fb.group(
        this.loanTypeOptions.reduce((acc, option) => {
          acc[this.sanitizeKey(option)] = [false];
          return acc;
        }, {} as any),
      ),
      willingToFillPrescreeningForm: [''],
      requestingLoanAmount: [
        '',
        [this.numberValidatorService.numberValidator('requestingLoanAmount')],
      ],
      loanTermMonths: [
        '',
        [this.numberValidatorService.numberValidator('loanTermMonths')],
      ],
      loanUse: this.fb.group(
        this.loanUseOptions.reduce((acc, option) => {
          acc[this.sanitizeKey(option)] = [false];
          return acc;
        }, {} as any),
      ),
      contributingPersonalCapital: [''],
      personalCapitalPercentage: [
        '',
        [
          this.numberValidatorService.numberValidator(
            'personalCapitalPercentage',
          ),
        ],
      ],
      collateralTypes: this.fb.group(
        this.collateralOptions.reduce((acc, option) => {
          acc[this.sanitizeKey(option)] = [false];
          return acc;
        }, {} as any),
      ),
      // Income or Revenue
      reportingPeriod: [''],
      monthsInPeriod: [
        '',
        [this.numberValidatorService.numberValidator('monthsInPeriod')],
      ],
      totalRevenue: [
        '',
        [this.numberValidatorService.numberValidator('totalRevenue')],
      ],
      netProfit: [
        '',
        [this.numberValidatorService.numberValidator('netProfit')],
      ],
      depreciation: [
        '',
        [this.numberValidatorService.numberValidator('depreciation')],
      ],
      // Assets and Working Capital
      totalAssets: [
        '',
        [this.numberValidatorService.numberValidator('totalAssets')],
      ],
      cashEquivalents: [
        '',
        [this.numberValidatorService.numberValidator('cashEquivalents')],
      ],
      tradeReceivables: [
        '',
        [this.numberValidatorService.numberValidator('tradeReceivables')],
      ],
      inventoryValue: [
        '',
        [this.numberValidatorService.numberValidator('inventoryValue')],
      ],
      totalCurrentLiabilities: [
        '',
        [
          this.numberValidatorService.numberValidator(
            'totalCurrentLiabilities',
          ),
        ],
      ],
      shareholderEquity: [
        '',
        [this.numberValidatorService.numberValidator('shareholderEquity')],
      ],
      alternativeNetWorth: [
        '',
        [this.numberValidatorService.numberValidator('alternativeNetWorth')],
      ],
      // Existing Debt
      outstandingLoans: [
        '',
        [this.numberValidatorService.numberValidator('outstandingLoans')],
      ],
      approvedCreditLimit: [
        '',
        [this.numberValidatorService.numberValidator('approvedCreditLimit')],
      ],
      monthlyDebtService: [
        '',
        [this.numberValidatorService.numberValidator('monthlyDebtService')],
      ],
      // Personal Financial Information
      personalIncome: [
        '',
        [this.numberValidatorService.numberValidator('personalIncome')],
      ],
      personalNetWorth: [
        '',
        [this.numberValidatorService.numberValidator('personalNetWorth')],
      ],
      personalLoans: [
        '',
        [this.numberValidatorService.numberValidator('personalLoans')],
      ],
      // Financial Documentation
      financialDocumentation: this.fb.group(
        this.financialDocOptions.reduce((acc, option) => {
          acc[this.sanitizeKey(option)] = [false];
          return acc;
        }, {} as any),
      ),
      // Simplified Form Fields
      creditScoreRange: [''],
      haveCoSigner: [''],
      haveBusinessPlan: [''],
      haveFinancialProjections: [''],
    });

    // Watch for loan type changes
    this.loanForm.get('loanType')?.valueChanges.subscribe((value) => {
      // Get selected loan types
      const selectedTypes = Object.entries(value)
        .filter(([, checked]) => checked === true)
        .map(([key]) =>
          this.loanTypeOptions.find((opt) => this.sanitizeKey(opt) === key),
        );

      // Show prescreening question unless ONLY 'Grant Programs' is selected
      const onlyGrantPrograms =
        selectedTypes.length === 1 && selectedTypes[0] === 'Grant Programs';
      const noSelection = selectedTypes.length === 0;
      this.showPrescreeningQuestion = !onlyGrantPrograms && !noSelection;

      // Reset prescreening answer and hide both forms when appropriate
      if (!this.showPrescreeningQuestion) {
        this.loanForm.get('willingToFillPrescreeningForm')?.setValue('');
        this.showFullForm = false;
        this.showSimplifiedForm = false;
        // Clear all full form and simplified form fields
        this.clearFullFormFields();
        this.clearSimplifiedFormFields();
        this.clearFinancialDocumentation();
      }
    });

    // Watch for prescreening form answer
    this.loanForm
      .get('willingToFillPrescreeningForm')
      ?.valueChanges.subscribe((value) => {
        // Show full form if user answered Yes, simplified form if No
        this.showFullForm = value === true;
        this.showSimplifiedForm = value === false;

        // Clear the opposite form's fields when switching
        if (value === true) {
          this.clearSimplifiedFormFields();
        } else if (value === false) {
          this.clearFullFormFields();
        }
      });

    // Watch for "No Collateral" checkbox changes
    this.loanForm
      .get('collateralTypes.noCollateral')
      ?.valueChanges.subscribe((checked) => {
        const collateralGroup = this.loanForm.get(
          'collateralTypes',
        ) as FormGroup;
        if (checked) {
          // Disable and uncheck all other collateral options
          this.collateralOptions.forEach((option) => {
            if (option !== 'No Collateral') {
              const key = this.sanitizeKey(option);
              collateralGroup.get(key)?.disable();
              collateralGroup.get(key)?.setValue(false);
            }
          });
        } else {
          // Enable all other collateral options
          this.collateralOptions.forEach((option) => {
            if (option !== 'No Collateral') {
              const key = this.sanitizeKey(option);
              collateralGroup.get(key)?.enable();
            }
          });
        }
      });

    // Watch for contributing personal capital changes
    this.loanForm
      .get('contributingPersonalCapital')
      ?.valueChanges.subscribe((value) => {
        this.showPersonalCapitalPercentage = value === true;
        if (value !== true) {
          this.loanForm.get('personalCapitalPercentage')?.setValue('');
        }
      });
  }

  ngOnInit(): void {
    if (this.initialValue) {
      // Convert array values back to checkbox object format
      const patchData = { ...this.initialValue };

      // Handle loanType array -> object conversion
      if (Array.isArray(patchData.loanType)) {
        const loanTypeObj: Record<string, boolean> = {};
        this.loanTypeOptions.forEach((option) => {
          const key = this.sanitizeKey(option);
          const backendValue = this.loanTypeLabelMap[option];
          loanTypeObj[key] = patchData.loanType.includes(backendValue);
        });
        patchData.loanType = loanTypeObj;
      }

      // Handle loanUse array -> object conversion
      if (Array.isArray(patchData.loanUse)) {
        const loanUseObj: Record<string, boolean> = {};
        this.loanUseOptions.forEach((option) => {
          const key = this.sanitizeKey(option);
          const backendValue = this.loanUseLabelMap[option];
          loanUseObj[key] = patchData.loanUse.includes(backendValue);
        });
        patchData.loanUse = loanUseObj;
      }

      // Handle collateralTypes array -> object conversion
      if (Array.isArray(patchData.collateralTypes)) {
        const collateralTypesObj: Record<string, boolean> = {};
        this.collateralOptions.forEach((option) => {
          const key = this.sanitizeKey(option);
          const backendValue = this.collateralLabelMap[option];
          collateralTypesObj[key] =
            patchData.collateralTypes.includes(backendValue);
        });
        patchData.collateralTypes = collateralTypesObj;
      }

      // Handle financialDocumentation array -> object conversion
      if (Array.isArray(patchData.financialDocumentation)) {
        const financialDocObj: Record<string, boolean> = {};
        this.financialDocOptions.forEach((option) => {
          const key = this.sanitizeKey(option);
          const backendValue = this.financialDocLabelMap[option];
          financialDocObj[key] =
            patchData.financialDocumentation.includes(backendValue);
        });
        patchData.financialDocumentation = financialDocObj;
      }

      // Handle credit score range reverse mapping
      if (patchData.creditScoreRange) {
        const reverseMap = Object.entries(this.creditScoreRangeMap).find(
          ([, value]) => value === patchData.creditScoreRange,
        );
        if (reverseMap) {
          patchData.creditScoreRange = reverseMap[0];
        }
      }

      this.loanForm.patchValue(patchData);

      // Restore ownership structure from either ownershipStructure or owner_ship_structure
      const ownershipData =
        this.initialValue.ownershipStructure ||
        this.initialValue.owner_ship_structure;
      if (
        ownershipData &&
        Array.isArray(ownershipData) &&
        ownershipData.length > 0
      ) {
        this.ownershipStructure = ownershipData.map((owner: any) => ({
          ownerName: owner.ownerName || owner.owner_name || '',
          ownershipPercentage:
            owner.ownershipPercentage || owner.ownership_percentage,
          otherBusinessesPercentage:
            owner.otherBusinessesPercentage || owner.other_business_percentage,
        }));
      }
    }

    // Patch creditScoreRange if provided from parent
    if (this.creditScoreRange) {
      let scoreVal = this.creditScoreRange;
      const reverseMap = Object.entries(this.creditScoreRangeMap).find(
        ([, value]) => value === this.creditScoreRange,
      );
      if (reverseMap) {
        scoreVal = reverseMap[0];
      }
      this.loanForm.patchValue({
        creditScoreRange: scoreVal,
      });
    }

    // Initialize with one empty row only if no data was loaded
    if (this.ownershipStructure.length === 0) {
      this.addOwnershipRow();
    }
  }

  sanitizeKey(option: string): string {
    return option
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .replace(/\s+/g, '');
  }

  addOwnershipRow(): void {
    this.ownershipStructure.push({
      ownerName: '',
      ownershipPercentage: undefined,
      otherBusinessesPercentage: undefined,
    });
  }

  removeOwnershipRow(index: number): void {
    if (this.ownershipStructure.length > 1) {
      this.ownershipStructure.splice(index, 1);
    }
  }

  isOwnershipStructureValid(): boolean {
    return this.ownershipStructure.every(
      (row) =>
        row.ownerName &&
        row.ownerName.trim() !== '' &&
        row.ownershipPercentage !== undefined &&
        row.ownershipPercentage !== null &&
        row.ownershipPercentage >= 0 &&
        row.ownershipPercentage <= 100 &&
        this.isValidDecimal(row.ownershipPercentage) &&
        (row.otherBusinessesPercentage === undefined ||
          row.otherBusinessesPercentage === null ||
          (row.otherBusinessesPercentage >= 0 &&
            row.otherBusinessesPercentage <= 100 &&
            this.isValidDecimal(row.otherBusinessesPercentage))),
    );
  }

  canAddOwner(): boolean {
    // Check if all existing rows have required fields filled
    return this.ownershipStructure.every(
      (row) =>
        row.ownerName &&
        row.ownerName.trim() !== '' &&
        row.ownershipPercentage !== undefined &&
        row.ownershipPercentage !== null &&
        row.ownershipPercentage >= 0 &&
        row.ownershipPercentage <= 100 &&
        this.isValidDecimal(row.ownershipPercentage),
    );
  }

  isValidDecimal(value: number | undefined | null): boolean {
    if (value === undefined || value === null) return true;
    const parts = String(value).split('.');
    if (parts.length < 2) return true;
    return parts[1].length <= 2;
  }

  private cleanEmptyOwnershipRows(): void {
    // Remove rows where owner name is empty or ownership percentage is not set
    this.ownershipStructure = this.ownershipStructure.filter(
      (row) =>
        row.ownerName &&
        row.ownerName.trim() !== '' &&
        row.ownershipPercentage !== undefined &&
        row.ownershipPercentage !== null,
    );

    // Ensure at least one row exists
    if (this.ownershipStructure.length === 0) {
      this.addOwnershipRow();
    }
  }

  private getValidOwnershipStructure(): any[] {
    // Return only valid ownership rows (non-empty)
    return this.ownershipStructure.filter(
      (row) =>
        row.ownerName &&
        row.ownerName.trim() !== '' &&
        row.ownershipPercentage !== undefined &&
        row.ownershipPercentage !== null,
    );
  }

  private clearFullFormFields(): void {
    // Clear all full form fields
    const fullFormFields = [
      'contributingPersonalCapital',
      'personalCapitalPercentage',
      'reportingPeriod',
      'monthsInPeriod',
      'netProfit',
      'depreciation',
      'totalAssets',
      'cashEquivalents',
      'tradeReceivables',
      'inventoryValue',
      'totalCurrentLiabilities',
      'shareholderEquity',
      'alternativeNetWorth',
      'outstandingLoans',
      'approvedCreditLimit',
      'monthlyDebtService',
      'personalIncome',
      'personalNetWorth',
      'personalLoans',
    ];

    fullFormFields.forEach((field) => {
      this.loanForm.get(field)?.setValue('');
    });

    // Clear loanUse checkboxes
    const loanUseGroup = this.loanForm.get('loanUse') as FormGroup;
    if (loanUseGroup) {
      Object.keys(loanUseGroup.controls).forEach((key) => {
        loanUseGroup.get(key)?.setValue(false);
      });
    }

    // Clear collateralTypes checkboxes
    const collateralGroup = this.loanForm.get('collateralTypes') as FormGroup;
    if (collateralGroup) {
      Object.keys(collateralGroup.controls).forEach((key) => {
        collateralGroup.get(key)?.setValue(false);
        collateralGroup.get(key)?.enable();
      });
    }
  }

  private clearSimplifiedFormFields(): void {
    // Clear all simplified form fields
    this.loanForm.get('creditScoreRange')?.setValue('');
    this.loanForm.get('haveCoSigner')?.setValue('');
    this.loanForm.get('haveBusinessPlan')?.setValue('');
    this.loanForm.get('haveFinancialProjections')?.setValue('');
  }

  private clearFinancialDocumentation(): void {
    // Clear financial documentation checkboxes
    const financialDocGroup = this.loanForm.get(
      'financialDocumentation',
    ) as FormGroup;
    if (financialDocGroup) {
      Object.keys(financialDocGroup.controls).forEach((key) => {
        financialDocGroup.get(key)?.setValue(false);
      });
    }
  }

  private extractSelectedLabels(
    groupValue: Record<string, boolean>,
    options: string[],
  ): string[] {
    return Object.entries(groupValue)
      .filter(([, checked]) => checked === true)
      .map(
        ([key]) =>
          options.find((opt) => this.sanitizeKey(opt) === key) as string,
      );
  }

  get hasErrors(): boolean {
    return (
      this.formSubmitted &&
      (this.loanForm.invalid ||
        (this.showFullForm && !this.isOwnershipStructureValid()))
    );
  }

  public onSubmitForm() {
    this.formSubmitted = true;

    // Clean empty ownership rows before submission
    this.cleanEmptyOwnershipRows();

    if (
      this.loanForm.invalid ||
      (this.showFullForm && !this.isOwnershipStructureValid())
    ) {
      this.markFormGroupTouched(this.loanForm);
      return;
    }

    const formValue = this.loanForm.value;

    const selectedLoanTypes = this.extractSelectedLabels(formValue.loanType, [
      ...this.loanTypeOptions,
    ]).map((label) => this.loanTypeLabelMap[label] ?? label);

    const selectedLoanUse = this.extractSelectedLabels(
      formValue.loanUse,
      this.loanUseOptions,
    ).map((label) => this.loanUseLabelMap[label] ?? label);

    const selectedCollateralTypes = this.extractSelectedLabels(
      formValue.collateralTypes,
      this.collateralOptions,
    ).map((label) => this.collateralLabelMap[label] ?? label);

    const selectedFinancialDocumentation = this.extractSelectedLabels(
      formValue.financialDocumentation,
      [...this.financialDocOptions],
    ).map((label) => this.financialDocLabelMap[label] ?? label);

    // Map credit score range
    const mappedCreditScoreRange = formValue.creditScoreRange
      ? (this.creditScoreRangeMap[formValue.creditScoreRange] ??
        formValue.creditScoreRange)
      : formValue.creditScoreRange;

    // Get only valid (non-empty) ownership rows
    const validOwnershipStructure = this.getValidOwnershipStructure();

    const payload = {
      ...formValue,
      loanType: selectedLoanTypes,
      loanUse: selectedLoanUse,
      collateralTypes: selectedCollateralTypes,
      financialDocumentation: selectedFinancialDocumentation,
      creditScoreRange: mappedCreditScoreRange,
      ownershipStructure: validOwnershipStructure,
      owner_ship_structure: validOwnershipStructure,
    };

    this.formSubmit.emit(payload);
  }

  private markFormGroupTouched(formGroup: FormGroup) {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getErrorMessage(fieldName: string): string {
    const control = this.loanForm.get(fieldName);
    if (!control) return '';
    if (control.hasError('required')) {
      return 'This field is required';
    }
    if (control.hasError('invalidNumber')) {
      return 'Invalid number';
    }
    if (control.hasError('min')) {
      const minVal = control.errors?.['min']?.min;
      return `Value must be at least ${minVal !== undefined ? minVal : control.errors?.['min']}`;
    }
    if (control.hasError('max')) {
      const maxVal = control.errors?.['max']?.max;
      return `Value must not exceed ${maxVal !== undefined ? maxVal : control.errors?.['max']}`;
    }
    if (control.hasError('pattern')) {
      return `Value must be a whole number`;
    }
    if (control.hasError('decimalPlaces')) {
      return `Must not exceed ${control.errors?.['decimalPlaces']?.max} decimal place(s)`;
    }
    if (control.hasError('maxDigits')) {
      const err = control.errors?.['maxDigits'];
      if (err?.isWholeDigits) {
        return `Must not exceed ${err.maxWholeDigits} whole number digit(s)`;
      }
      return `Must not exceed ${err.max} total digit(s)`;
    }
    return '';
  }
}
